import { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography, Button, Backdrop } from '@mui/material';

const FacebookSDKLoader = ({ children }) => {
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [sdkError, setSdkError] = useState(false);
  const [retryCount] = useState(() => {
    // 从localStorage获取重试次数
    const savedCount = localStorage.getItem('fb_sdk_retry_count');
    return savedCount ? parseInt(savedCount, 10) : 0;
  });
  const MAX_RETRIES = 3;

  const loadFacebookSDK = () => {
    // 如果SDK已经加载，直接返回
    if (window.FB) {
      setSdkLoaded(true);
      // 成功加载后清除重试计数
      localStorage.removeItem('fb_sdk_retry_count');
      return;
    }

    // 如果已经超过最大重试次数，直接显示错误
    if (retryCount >= MAX_RETRIES) {
      setSdkError(true);
      return;
    }

    // 设置加载超时
    const timeout = setTimeout(() => {
      if (!window.FB) {
        handleSDKError();
      }
    }, 10000); // 10秒超时

    // 监听SDK初始化事件
    const handleSDKInit = () => {
      clearTimeout(timeout);
      setSdkLoaded(true);
      setSdkError(false);
      // 成功加载后清除重试计数
      localStorage.removeItem('fb_sdk_retry_count');
    };
    
    window.addEventListener('fb-sdk-initialized', handleSDKInit);
  };

  const handleSDKError = () => {
    if (retryCount < MAX_RETRIES) {
      const newCount = retryCount + 1;
      // 保存重试次数到localStorage
      localStorage.setItem('fb_sdk_retry_count', newCount.toString());
      
      // 重新加载页面
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } else {
      // 超过最大重试次数，显示错误
      setSdkError(true);
    }
  };

  const resetRetryCount = () => {
    localStorage.removeItem('fb_sdk_retry_count');
    window.location.reload();
  };

  useEffect(() => {
    loadFacebookSDK();
  }, [retryCount]);

  return (
    <>
      {/* 总是渲染children（商店列表等内容） */}
      {children}
      
      {/* Loading覆盖层 */}
      {!sdkLoaded && !sdkError && (
        <Backdrop
          open={true}
          sx={{
            zIndex: 9999,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
        >
          <CircularProgress 
            size={80} 
            thickness={4}
            sx={{ 
              color: '#1877f2',
              mb: 2
            }} 
          />
          <Typography 
            variant="h5" 
            sx={{ 
              color: '#fff',
              fontWeight: 500,
              mb: 1,
              textShadow: '0 2px 4px rgba(0,0,0,0.5)'
            }}
          >
            Loading...
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#fff',
              opacity: 0.9,
              textShadow: '0 2px 4px rgba(0,0,0,0.5)'
            }}
          >
            Preparing your experience
          </Typography>
          {retryCount > 0 && (
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#fff',
                opacity: 0.7,
                mt: 1
              }}
            >
              Retry attempt {retryCount} of {MAX_RETRIES}
            </Typography>
          )}
        </Backdrop>
      )}

      {/* 错误覆盖层 */}
      {sdkError && (
        <Backdrop
          open={true}
          sx={{
            zIndex: 9999,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: 2,
              p: 4,
              maxWidth: 500,
              width: '90%',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="h4" 
                gutterBottom
                sx={{ 
                  color: '#d32f2f',
                  fontWeight: 'bold',
                  mb: 2
                }}
              >
                ⚠️ Network Error
              </Typography>
              <Typography 
                variant="h6"
                sx={{ 
                  color: '#333',
                  lineHeight: 1.6
                }}
              >
                Unable to connect to Facebook services.
              </Typography>
              <Typography 
                variant="body1"
                sx={{ 
                  color: '#666',
                  mt: 1
                }}
              >
                Please check your internet connection and try again.
              </Typography>
            </Box>
            
            <Button 
              variant="contained" 
              color="primary" 
              onClick={resetRetryCount}
              size="large"
              sx={{ 
                px: 5,
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 'bold',
                backgroundColor: '#1877f2',
                '&:hover': {
                  backgroundColor: '#166fe5'
                }
              }}
            >
              Retry Connection
            </Button>
            
            <Typography 
              variant="caption"
              sx={{ 
                color: '#999',
                mt: 1
              }}
            >
              Failed after 3 attempts
            </Typography>
          </Box>
        </Backdrop>
      )}
    </>
  );
};

export default FacebookSDKLoader;
